// ============================================================
// Sound Effects — built-in synthesized + custom audio files
// ============================================================

export type SoundType = 'buzz' | 'correct' | 'incorrect';

let audioCtx: AudioContext | null = null;

function getAudioContext(): AudioContext {
  if (!audioCtx) {
    audioCtx = new AudioContext();
  }
  if (audioCtx.state === 'suspended') {
    audioCtx.resume();
  }
  return audioCtx;
}

// ============================================================
// Built-in synthesized sounds (per type, per style)
// ============================================================

// --- Default ---
function playBuzzDefault(): void {
  const ctx = getAudioContext();
  const now = ctx.currentTime;
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.connect(gain); gain.connect(ctx.destination);
  osc.type = 'square';
  osc.frequency.setValueAtTime(880, now);
  osc.frequency.exponentialRampToValueAtTime(440, now + 0.1);
  gain.gain.setValueAtTime(0.3, now);
  gain.gain.exponentialRampToValueAtTime(0.01, now + 0.15);
  osc.start(now); osc.stop(now + 0.15);
}

function playCorrectDefault(): void {
  const ctx = getAudioContext();
  const now = ctx.currentTime;
  const osc1 = ctx.createOscillator();
  const gain1 = ctx.createGain();
  osc1.connect(gain1); gain1.connect(ctx.destination);
  osc1.type = 'sine';
  osc1.frequency.setValueAtTime(523.25, now);
  gain1.gain.setValueAtTime(0.4, now);
  gain1.gain.exponentialRampToValueAtTime(0.01, now + 0.2);
  osc1.start(now); osc1.stop(now + 0.2);
  const osc2 = ctx.createOscillator();
  const gain2 = ctx.createGain();
  osc2.connect(gain2); gain2.connect(ctx.destination);
  osc2.type = 'sine';
  osc2.frequency.setValueAtTime(783.99, now + 0.15);
  gain2.gain.setValueAtTime(0.01, now);
  gain2.gain.setValueAtTime(0.4, now + 0.15);
  gain2.gain.exponentialRampToValueAtTime(0.01, now + 0.5);
  osc2.start(now + 0.15); osc2.stop(now + 0.5);
}

function playIncorrectDefault(): void {
  const ctx = getAudioContext();
  const now = ctx.currentTime;
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.connect(gain); gain.connect(ctx.destination);
  osc.type = 'sawtooth';
  osc.frequency.setValueAtTime(120, now);
  gain.gain.setValueAtTime(0.3, now);
  gain.gain.setValueAtTime(0.3, now + 0.4);
  gain.gain.exponentialRampToValueAtTime(0.01, now + 0.6);
  osc.start(now); osc.stop(now + 0.6);
  const osc2 = ctx.createOscillator();
  const gain2 = ctx.createGain();
  osc2.connect(gain2); gain2.connect(ctx.destination);
  osc2.type = 'sawtooth';
  osc2.frequency.setValueAtTime(93, now);
  gain2.gain.setValueAtTime(0.15, now);
  gain2.gain.setValueAtTime(0.15, now + 0.4);
  gain2.gain.exponentialRampToValueAtTime(0.01, now + 0.6);
  osc2.start(now); osc2.stop(now + 0.6);
}

// --- Retro (8-bit) ---
function playBuzzRetro(): void {
  const ctx = getAudioContext();
  const now = ctx.currentTime;
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.connect(gain); gain.connect(ctx.destination);
  osc.type = 'square';
  osc.frequency.setValueAtTime(1200, now);
  osc.frequency.setValueAtTime(800, now + 0.05);
  osc.frequency.setValueAtTime(1000, now + 0.1);
  gain.gain.setValueAtTime(0.25, now);
  gain.gain.exponentialRampToValueAtTime(0.01, now + 0.15);
  osc.start(now); osc.stop(now + 0.15);
}

function playCorrectRetro(): void {
  const ctx = getAudioContext();
  const now = ctx.currentTime;
  const notes = [523, 659, 784, 1047];
  notes.forEach((freq, i) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain); gain.connect(ctx.destination);
    osc.type = 'square';
    const t = now + i * 0.08;
    osc.frequency.setValueAtTime(freq, t);
    gain.gain.setValueAtTime(0.01, now);
    gain.gain.setValueAtTime(0.25, t);
    gain.gain.exponentialRampToValueAtTime(0.01, t + 0.12);
    osc.start(t); osc.stop(t + 0.12);
  });
}

function playIncorrectRetro(): void {
  const ctx = getAudioContext();
  const now = ctx.currentTime;
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.connect(gain); gain.connect(ctx.destination);
  osc.type = 'square';
  osc.frequency.setValueAtTime(200, now);
  osc.frequency.setValueAtTime(100, now + 0.2);
  gain.gain.setValueAtTime(0.25, now);
  gain.gain.setValueAtTime(0.25, now + 0.35);
  gain.gain.exponentialRampToValueAtTime(0.01, now + 0.5);
  osc.start(now); osc.stop(now + 0.5);
}

// --- Gentle ---
function playBuzzGentle(): void {
  const ctx = getAudioContext();
  const now = ctx.currentTime;
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.connect(gain); gain.connect(ctx.destination);
  osc.type = 'sine';
  osc.frequency.setValueAtTime(660, now);
  gain.gain.setValueAtTime(0, now);
  gain.gain.linearRampToValueAtTime(0.3, now + 0.02);
  gain.gain.exponentialRampToValueAtTime(0.01, now + 0.2);
  osc.start(now); osc.stop(now + 0.2);
}

function playCorrectGentle(): void {
  const ctx = getAudioContext();
  const now = ctx.currentTime;
  [523, 659, 784].forEach((freq) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain); gain.connect(ctx.destination);
    osc.type = 'sine';
    osc.frequency.setValueAtTime(freq, now);
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(0.2, now + 0.05);
    gain.gain.exponentialRampToValueAtTime(0.01, now + 0.6);
    osc.start(now); osc.stop(now + 0.6);
  });
}

function playIncorrectGentle(): void {
  const ctx = getAudioContext();
  const now = ctx.currentTime;
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.connect(gain); gain.connect(ctx.destination);
  osc.type = 'sine';
  osc.frequency.setValueAtTime(180, now);
  osc.frequency.exponentialRampToValueAtTime(140, now + 0.4);
  gain.gain.setValueAtTime(0, now);
  gain.gain.linearRampToValueAtTime(0.25, now + 0.03);
  gain.gain.exponentialRampToValueAtTime(0.01, now + 0.5);
  osc.start(now); osc.stop(now + 0.5);
}

// ============================================================
// Built-in registry: choice ID -> play function, per sound type
// ============================================================

const builtinSounds: Record<SoundType, Record<string, () => void>> = {
  buzz: {
    default: playBuzzDefault,
    retro: playBuzzRetro,
    gentle: playBuzzGentle,
  },
  correct: {
    default: playCorrectDefault,
    retro: playCorrectRetro,
    gentle: playCorrectGentle,
  },
  incorrect: {
    default: playIncorrectDefault,
    retro: playIncorrectRetro,
    gentle: playIncorrectGentle,
  },
};

export const BUILTIN_CHOICES = [
  { id: 'default', name: 'デフォルト' },
  { id: 'retro', name: 'レトロ (8bit風)' },
  { id: 'gentle', name: 'やさしい' },
];

// ============================================================
// Custom sound file support
// ============================================================

export interface CustomSoundEntry {
  id: string;
  name: string;
  file: string;
}

export interface SoundManifest {
  buzz: CustomSoundEntry[];
  correct: CustomSoundEntry[];
  incorrect: CustomSoundEntry[];
}

const audioBufferCache = new Map<string, AudioBuffer>();
const loadingPromises = new Map<string, Promise<AudioBuffer | null>>();

let manifest: SoundManifest = { buzz: [], correct: [], incorrect: [] };
let manifestLoaded = false;

export async function loadSoundManifest(): Promise<SoundManifest> {
  if (manifestLoaded) return manifest;
  try {
    const res = await fetch('/public/sounds/manifest.json');
    if (res.ok) {
      manifest = await res.json();
    }
  } catch {
    // manifest not available
  }
  manifestLoaded = true;
  return manifest;
}

export function getSoundManifest(): SoundManifest {
  return manifest;
}

async function loadAudioBuffer(url: string): Promise<AudioBuffer | null> {
  if (audioBufferCache.has(url)) return audioBufferCache.get(url)!;
  if (loadingPromises.has(url)) return loadingPromises.get(url)!;

  const promise = (async () => {
    try {
      const ctx = getAudioContext();
      const res = await fetch(url);
      if (!res.ok) return null;
      const arrayBuffer = await res.arrayBuffer();
      const buffer = await ctx.decodeAudioData(arrayBuffer);
      audioBufferCache.set(url, buffer);
      return buffer;
    } catch {
      return null;
    } finally {
      loadingPromises.delete(url);
    }
  })();

  loadingPromises.set(url, promise);
  return promise;
}

function playAudioBuffer(buffer: AudioBuffer): void {
  const ctx = getAudioContext();
  const source = ctx.createBufferSource();
  source.buffer = buffer;
  source.connect(ctx.destination);
  source.start(0);
}

/** Preload a custom sound file */
export async function preloadCustomSound(soundId: string, type: SoundType): Promise<void> {
  const entries = manifest[type];
  const entry = entries.find(e => e.id === soundId);
  if (entry) {
    await loadAudioBuffer(`/public/sounds/${entry.file}`);
  }
}

// ============================================================
// Public API
// ============================================================

/**
 * Play a sound by type and choice ID.
 * choice can be a built-in ID ('default', 'retro', 'gentle') or a custom ID from manifest.
 */
export function playSound(type: SoundType, choice: string = 'default'): void {
  // Try built-in
  const builtinFn = builtinSounds[type][choice];
  if (builtinFn) {
    builtinFn();
    return;
  }

  // Try custom from manifest
  const entries = manifest[type];
  const entry = entries?.find(e => e.id === choice);
  if (entry) {
    const url = `/public/sounds/${entry.file}`;
    const cached = audioBufferCache.get(url);
    if (cached) {
      playAudioBuffer(cached);
    } else {
      loadAudioBuffer(url).then(buf => { if (buf) playAudioBuffer(buf); });
    }
    return;
  }

  // Fallback to default
  builtinSounds[type].default();
}

export function isBuiltinChoice(choice: string): boolean {
  return choice in builtinSounds.buzz;
}

// Legacy
export function playSoundByPreset(type: SoundType, preset: string = 'default'): void {
  playSound(type, preset);
}

export function initAudio(): void {
  const ctx = getAudioContext();
  // Play a silent buffer to unlock audio on iOS.
  // This must be called from a user gesture (tap/click).
  if (ctx.state === 'suspended') {
    ctx.resume();
  }
  const silent = ctx.createBuffer(1, 1, ctx.sampleRate);
  const src = ctx.createBufferSource();
  src.buffer = silent;
  src.connect(ctx.destination);
  src.start();
}
